/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { zodResolver } from '@hookform/resolvers/zod';
import {
  CLIENT_DIAGNOSES,
  CreateTherapistSchema,
  THERAPIST_CATEGORIES,
  type CreateTherapistInput,
} from '@therapy/shared-validators';
import { type ChangeEvent, type SyntheticEvent, useCallback, type JSX } from 'react';
import {
  type Control,
  type FieldErrors,
  useController,
  useForm,
  type UseFormRegister,
  useWatch,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FormField } from '../../components/form/form-field.js';
import { Button } from '../../components/ui/button.js';
import { type Therapist } from '../../lib/api/resources.js';
import { useCreateTherapist, useUpdateTherapist } from '../../lib/queries/clinical.js';
import { rupeesToPaise } from '../../lib/utils/money.js';

interface TherapistFormProps {
  readonly onDone: () => void;
  /** When set, the form edits this therapist instead of creating a new one. */
  readonly therapist?: Therapist;
}

type Ctrl = Control<CreateTherapistInput>;
const FIELD_LABEL_CLASS = 'mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300';
const FIELD_INPUT_CLASS =
  'focus-ring w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-white';

/** A chosen specialty maps to the `specialisations` array (empty → undefined). */
function toSpecialisations(value: unknown): string[] | undefined {
  return typeof value === 'string' && value.trim() !== '' ? [value] : undefined;
}

/** A rupee money input bound to a paise field (paise ⇄ rupees both ways). */
function RupeeField({
  control,
  name,
  label,
  error,
}: {
  readonly control: Ctrl;
  readonly name: 'perSessionRatePaise' | 'monthlyFixedPaise';
  readonly label: string;
  readonly error?: string | undefined;
}): JSX.Element {
  const { field } = useController({ control, name });
  const paise = field.value;
  const display = typeof paise === 'number' ? String(paise / 100) : '';
  const onChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value;
      field.onChange(raw === '' ? undefined : rupeesToPaise(Number(raw)));
    },
    [field],
  );
  return (
    <div>
      <label htmlFor={name} className={FIELD_LABEL_CLASS}>
        {label}
      </label>
      <input
        id={name}
        type="number"
        min="0"
        step="1"
        inputMode="numeric"
        className={FIELD_INPUT_CLASS}
        value={display}
        onChange={onChange}
        onBlur={field.onBlur}
        aria-invalid={error !== undefined}
      />
      {error !== undefined && (
        <p role="alert" className="mt-1 text-xs text-red-600">
          {error}
        </p>
      )}
    </div>
  );
}

function editDefaults(t: Therapist): Partial<CreateTherapistInput> {
  return {
    fullName: t.fullName,
    email: t.email,
    category: t.category,
    ...(t.phone !== null ? { phone: t.phone } : {}),
    ...(t.perSessionRatePaise !== null ? { perSessionRatePaise: t.perSessionRatePaise } : {}),
    ...(t.monthlyFixedPaise !== null ? { monthlyFixedPaise: t.monthlyFixedPaise } : {}),
  };
}

interface FieldsProps {
  readonly register: UseFormRegister<CreateTherapistInput>;
  readonly control: Ctrl;
  readonly errors: FieldErrors<CreateTherapistInput>;
  readonly category: string | undefined;
  readonly initialSpecialty: string;
}

/** All therapist inputs (identity + engagement). Split out to keep the form lean. */
function TherapistFields({
  register,
  control,
  errors,
  category,
  initialSpecialty,
}: FieldsProps): JSX.Element {
  const { t } = useTranslation();
  return (
    <>
      <FormField
        id="therapist-fullName"
        type="text"
        label={t('form.therapist.fullName')}
        autoComplete="name"
        error={errors.fullName?.message}
        registration={register('fullName')}
      />
      <div>
        <label htmlFor="therapist-category" className={FIELD_LABEL_CLASS}>
          {t('form.therapist.category')}
        </label>
        <select id="therapist-category" className={FIELD_INPUT_CLASS} {...register('category')}>
          {THERAPIST_CATEGORIES.map((value) => (
            <option key={value} value={value}>
              {t(`form.therapist.categoryOption.${value}`)}
            </option>
          ))}
        </select>
      </div>
      <RupeeField
        control={control}
        name="perSessionRatePaise"
        label={t('form.therapist.perSessionRate')}
        error={errors.perSessionRatePaise?.message}
      />
      {category === 'on_roll' && (
        <RupeeField
          control={control}
          name="monthlyFixedPaise"
          label={t('form.therapist.monthlyFixed')}
          error={errors.monthlyFixedPaise?.message}
        />
      )}
      <div>
        <label htmlFor="therapist-specialisations" className={FIELD_LABEL_CLASS}>
          {t('form.therapist.specialty')}
        </label>
        <select
          id="therapist-specialisations"
          className={FIELD_INPUT_CLASS}
          defaultValue={initialSpecialty}
          aria-invalid={errors.specialisations !== undefined}
          {...register('specialisations', { setValueAs: toSpecialisations })}
        >
          <option value="">{t('form.therapist.selectSpecialty')}</option>
          {CLIENT_DIAGNOSES.map((value) => (
            <option key={value} value={value}>
              {value}
            </option>
          ))}
        </select>
      </div>
      <FormField
        id="therapist-email"
        type="email"
        label={t('form.therapist.email')}
        autoComplete="email"
        error={errors.email?.message}
        registration={register('email')}
      />
      <FormField
        id="therapist-phone"
        type="tel"
        label={t('form.therapist.phone')}
        autoComplete="tel"
        error={errors.phone?.message}
        registration={register('phone')}
      />
    </>
  );
}

/** The error line + cancel/submit buttons; computes busy/failed from both mutations. */
function FormFooter({
  create,
  update,
  isEdit,
  isSubmitting,
  onDone,
}: {
  readonly create: { isPending: boolean; isError: boolean };
  readonly update: { isPending: boolean; isError: boolean };
  readonly isEdit: boolean;
  readonly isSubmitting: boolean;
  readonly onDone: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const busy = isSubmitting || create.isPending || update.isPending;
  const failed = create.isError || update.isError;
  return (
    <>
      {failed && (
        <p role="alert" className="text-sm text-red-600">
          {t('common.saveError')}
        </p>
      )}
      <div className="flex justify-end gap-3 pt-2">
        <Button type="button" variant="outline" onClick={onDone}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" disabled={busy}>
          {isEdit ? t('common.save') : t('form.therapist.submit')}
        </Button>
      </div>
    </>
  );
}

function firstSpecialty(therapist?: Therapist): string {
  return therapist?.specialisations[0] ?? '';
}

/** "Add / Edit Therapist" form (Figma). Validated by the shared `CreateTherapistSchema`. */
export function TherapistForm({ onDone, therapist }: TherapistFormProps): JSX.Element {
  const isEdit = therapist !== undefined;
  const create = useCreateTherapist();
  const update = useUpdateTherapist(isEdit ? therapist.id : '');
  const {
    control,
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<CreateTherapistInput>({
    resolver: zodResolver(CreateTherapistSchema),
    defaultValues: isEdit ? editDefaults(therapist) : { category: 'on_roll' },
  });
  const category = useWatch({ control, name: 'category' });

  const submit = useCallback(
    async (values: CreateTherapistInput): Promise<void> => {
      await (isEdit ? update.mutateAsync(values) : create.mutateAsync(values));
      onDone();
    },
    [isEdit, update, create, onDone],
  );
  const onFormSubmit = useCallback(
    (event: SyntheticEvent<HTMLFormElement>): void => {
      void handleSubmit(submit)(event);
    },
    [handleSubmit, submit],
  );

  return (
    <form onSubmit={onFormSubmit} className="space-y-4" noValidate>
      <TherapistFields
        register={register}
        control={control}
        errors={errors}
        category={category}
        initialSpecialty={firstSpecialty(therapist)}
      />
      <FormFooter
        create={create}
        update={update}
        isEdit={isEdit}
        isSubmitting={isSubmitting}
        onDone={onDone}
      />
    </form>
  );
}
